// depends_on_controller.js
// Replaces jQuery depends_on.js for conditional field visibility
//
// Usage:
//   <div data-controller="depends-on"
//        data-depends-on-source-value="#field_id"
//        data-depends-on-expected-value="some_value">
//     Field content...
//   </div>
//
// Supports:
// - Checkboxes (shows when checked)
// - Select dropdowns (shows when value matches)
// - Multiple expected values (array)
// - Show when not empty (no expected value)

import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

export default class extends Controller {
  static values = {
    source: String,        // Selector for the field to watch (e.g., "#field_id" or "[name='model[field]']")
    expected: String,      // Expected value to show (optional - if omitted, shows when not empty)
    multiple: Boolean      // If true, expected is treated as comma-separated list
  }

  connect() {
    // Find the source field
    this.sourceField = this.findSourceField()

    if (!this.sourceField) {
      logger.warn(`DependsOn controller: Source field not found: ${this.sourceValue}`)
      return
    }

    // Listen to changes
    this.sourceField.addEventListener('change', this.toggle.bind(this))

    // Set initial state
    this.toggle()
  }

  disconnect() {
    if (this.sourceField) {
      this.sourceField.removeEventListener('change', this.toggle.bind(this))
    }
  }

  findSourceField() {
    // Try as selector first (ID or name)
    let field = document.querySelector(this.sourceValue)

    // If not found, try adding # for ID
    if (!field && !this.sourceValue.startsWith('#') && !this.sourceValue.startsWith('[')) {
      field = document.getElementById(this.sourceValue)
    }

    return field
  }

  toggle() {
    const shouldShow = this.shouldShowElement()

    if (shouldShow) {
      this.element.style.display = ''
      this.element.style.removeProperty('display')
    } else {
      this.element.style.display = 'none'
    }
  }

  shouldShowElement() {
    if (!this.sourceField) return false

    // Handle checkboxes
    if (this.sourceField.type === 'checkbox') {
      return this.sourceField.checked
    }

    // Handle radio buttons
    if (this.sourceField.type === 'radio') {
      const checkedRadio = document.querySelector(`input[name="${this.sourceField.name}"]:checked`)
      if (!checkedRadio) return false
      return this.matchesExpectedValue(checkedRadio.value)
    }

    // Handle select and other inputs
    const fieldValue = this.getFieldValue()

    // If no expected value provided, show when field is not empty
    if (!this.hasExpectedValue) {
      return fieldValue && fieldValue.trim() !== ''
    }

    // Match against expected value(s)
    return this.matchesExpectedValue(fieldValue)
  }

  getFieldValue() {
    if (this.sourceField.tagName === 'SELECT') {
      const selected = this.sourceField.options[this.sourceField.selectedIndex]
      return selected ? selected.value : ''
    }
    return this.sourceField.value
  }

  matchesExpectedValue(fieldValue) {
    if (!fieldValue) return false

    // Handle multiple expected values (comma-separated)
    if (this.multipleValue || this.expectedValue.includes(',')) {
      const expectedValues = this.expectedValue.split(',').map(v => v.trim())
      return expectedValues.some(expected =>
        expected.toString() === fieldValue.toString()
      )
    }

    // Single value comparison
    return this.expectedValue.toString() === fieldValue.toString()
  }

  get hasExpectedValue() {
    // Check if expectedValue was provided (via data attribute)
    try {
      return this.expectedValue && this.expectedValue.trim() !== ''
    } catch {
      return false
    }
  }
}
